import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Eye, FolderOpen, FolderPlus } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardContent,
  PermissionGuard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";

import * as clientsApi from "../clients/clients.api";
import { TClientExtendedResponse } from "../clients/clients.types";
import * as projectsApi from "./projects.api";
import { TProjectListItem } from "./projects.types";

export const ProjectsListPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  // Buscar dados do cliente para exibir o nome
  const { data: client } = useQuery<TClientExtendedResponse>({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.getClientById(clientId!),
    enabled: !!clientId,
    staleTime: 60_000,
  });

  // Buscar projetos da organização
  const {
    data: projects = [],
    isLoading,
    error,
  } = useQuery<TProjectListItem[]>({
    queryKey: ["projects", clientId],
    queryFn: async () => {
      if (!clientId) {
        throw new Error("clientId não encontrado");
      }
      
      return await projectsApi.getProjectsByOrganization(clientId);
    },
    enabled: !!clientId,
    staleTime: 60_000,
  });

  const getTypeClientBadgeVariant = (type: "affinity" | "affiliate") => {
    switch (type) {
      case "affinity":
        return "default";
      case "affiliate":
        return "secondary";
      default:
        return "outline";
    }
  };

  const getTypeClientText = (type: "affinity" | "affiliate") => {
    switch (type) {
      case "affinity":
        return "Afinidade";
      case "affiliate":
        return "Afiliados";
      default:
        return type;
    }
  };

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                navigate(`/clients`, {
                  state: { openClientId: clientId },
                })
              }
              size="sm"
              className="p-3 hover:bg-muted/50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Projetos
            </h1>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {client?.name
              ? `Projetos de ${client.name}`
              : "Lista de projetos desta organização"}
          </p>
        </div>
        <PermissionGuard permission="project:create">
          <Button
            type="button"
            onClick={() => navigate(`/clients/${clientId}/projects`)}
            className="gap-2"
          >
            <FolderPlus className="h-4 w-4" />
            Criar Projeto
          </Button>
        </PermissionGuard>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Carregando projetos...</p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 space-y-2">
            <p className="text-destructive font-medium">
              Erro ao carregar projetos.
            </p>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : "Tente novamente."}
            </p>
            {clientId && (
              <p className="text-xs text-muted-foreground">
                ID da organização: {clientId}
              </p>
            )}
          </CardContent>
        </Card>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              Nenhum projeto encontrado para esta organização.
            </p>
            <PermissionGuard permission="project:create">
              <Button
                onClick={() => navigate(`/clients/${clientId}/projects`)}
                variant="outline"
              >
                Criar Primeiro Projeto
              </Button>
            </PermissionGuard>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome do Projeto</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Hunter</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => (
                  <TableRow
                    key={project.project_id}
                    className="hover:bg-muted/50 transition-colors"
                  >
                    <TableCell className="font-medium">
                      {project.project_name}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getTypeClientBadgeVariant(project.type_client)}
                      >
                        {getTypeClientText(project.type_client)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {project.hunter ? (
                        <Badge variant="default">Sim</Badge>
                      ) : (
                        <Badge variant="outline">Não</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end">
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={() => {
                            navigate(`/clients/${clientId}/projects/${project.project_id}`);
                          }}
                          className="gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          Ver Detalhes
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

